import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';
import { invoiceDetailQueryKey, type InvoiceLineRow } from '@/hooks/useInvoices';
import { patientAppointmentsQueryKey } from '@/hooks/usePatientAppointments';
import { appointmentDetailQueryKey } from '@/hooks/useAppointments';

export type AppointmentStatus = Database['public']['Enums']['appointment_status'];

export interface UninvoicedAppointment {
  id: string;
  starts_at: string;
  ends_at: string;
  status: AppointmentStatus;
  service_id: string | null;
  service_name: string;
  price_cents: number;
  vat_rate_percent: number;
  clinician_name: string | null;
}

export interface AppointmentInvoiceLink {
  appointment_id: string;
  invoice_id: string;
  invoice_number: string | null;
  invoice_status: Database['public']['Enums']['invoice_status'];
}

export const uninvoicedAppointmentsKey = (
  patientId: string | null | undefined,
  clinicId: string | null | undefined,
) => ['invoices', 'uninvoiced-appointments', patientId ?? null, clinicId ?? null] as const;

export function useUninvoicedAppointments(
  patientId: string | null | undefined,
  clinicId: string | null | undefined,
) {
  return useQuery({
    queryKey: uninvoicedAppointmentsKey(patientId, clinicId),
    enabled: Boolean(patientId) && Boolean(clinicId),
    queryFn: async (): Promise<UninvoicedAppointment[]> => {
      if (!patientId || !clinicId) return [];
      const { data, error } = await supabase.rpc('list_uninvoiced_appointments_for_patient', {
        p_patient_id: patientId,
        p_clinic_id: clinicId,
      });
      if (error) throw error;
      return (data ?? []) as UninvoicedAppointment[];
    },
    staleTime: 10_000,
  });
}

export function useAddAppointmentInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      invoice_id: string;
      appointment_id: string;
      patient_id: string;
      clinic_id: string;
    }): Promise<InvoiceLineRow> => {
      const { data, error } = await supabase.rpc('add_appointment_invoice_line', {
        p_invoice_id: input.invoice_id,
        p_appointment_id: input.appointment_id,
      });
      if (error) throw error;
      return data as unknown as InvoiceLineRow;
    },
    onSuccess: (row, input) => {
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.invoice_id) });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      qc.invalidateQueries({ queryKey: uninvoicedAppointmentsKey(input.patient_id, input.clinic_id) });
      qc.invalidateQueries({ queryKey: patientAppointmentsQueryKey(input.patient_id) });
      // Auto-advance visit status to treatment_finished when invoiced
      void (async () => {
        try {
          await supabase.rpc('set_appointment_visit_status', {
            p_appointment_id: input.appointment_id,
            p_visit_status: 'treatment_finished',
          });
          qc.invalidateQueries({ queryKey: ['appointments'] });
          qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(input.appointment_id) });
        } catch {
          // Non-critical: appointment may already be completed/cancelled
        }
      })();
    },
  });
}

export async function fetchAppointmentInvoiceLinks(
  appointmentIds: string[],
): Promise<Map<string, AppointmentInvoiceLink>> {
  if (appointmentIds.length === 0) return new Map();

  const { data, error } = await supabase
    .from('invoice_line_items')
    .select('source_id, invoice_id, invoices!inner(id, invoice_number, status)')
    .eq('source', 'appointment')
    .in('source_id', appointmentIds)
    .neq('invoices.status', 'void');

  if (error) throw error;

  const map = new Map<string, AppointmentInvoiceLink>();
  for (const row of data ?? []) {
    const sourceId = row.source_id as string | null;
    if (!sourceId) continue;
    const inv = row.invoices as unknown as {
      id: string;
      invoice_number: string | null;
      status: Database['public']['Enums']['invoice_status'];
    };
    map.set(sourceId, {
      appointment_id: sourceId,
      invoice_id: inv.id,
      invoice_number: inv.invoice_number,
      invoice_status: inv.status,
    });
  }
  return map;
}
